import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Users, TrendingUp, Crown, ShieldCheck, RefreshCw, EyeOff, Eye, UserCheck, Copy, Check } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

// Read-only admin dashboard at /admin/users.
// Gated by the same role === "admin" check the backend uses. If a non-admin
// somehow lands on this route they get a 403 screen (double defence).
//
// Displays user counts + a recent-signups table. NEVER shows passwords or
// profile/health data — the backend endpoints don't return those fields at all.

function StatCard({ icon: Icon, label, value, testId, hint }) {
  return (
    <div
      data-testid={testId}
      style={{
        background: "var(--bg-elev-1, #141414)",
        border: "1px solid var(--line, #1f1f1f)",
        borderRadius: 14,
        padding: "18px 20px",
        display: "flex", flexDirection: "column", gap: 10,
        minWidth: 0,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, color: "var(--text-dim)" }}>
        <Icon size={14} strokeWidth={1.7} />
        <span
          className="eyebrow"
          style={{ fontSize: 10, letterSpacing: ".22em" }}
        >
          {label}
        </span>
      </div>
      <div
        style={{
          fontFamily: "'Fraunces', serif",
          fontSize: 32,
          color: "var(--text)",
          lineHeight: 1.1,
        }}
      >
        {value}
      </div>
      {hint && (
        <div className="tiny" style={{ color: "var(--text-faint)", marginTop: -4 }}>
          {hint}
        </div>
      )}
    </div>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      year: "numeric", month: "short", day: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function AdminUsersScreen() {
  const navigate = useNavigate();
  const { user } = useAuth() || {};

  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [includeTest, setIncludeTest] = useState(false);
  const [maskEmails, setMaskEmails] = useState(false);

  // -----------------------------------------
  // Apple / Play reviewer provisioning state.
  // Lets the admin re-seed the reviewer account with one click, so we never
  // have to run curl commands right before an App Store submission.
  // -----------------------------------------
  const REVIEWER_EMAIL = "apple.reviewer@ixnovaai.com";
  const REVIEWER_PASSWORD = "AppReview-Nova-2026!";
  const [reviewerBusy, setReviewerBusy] = useState(false);
  const [reviewerMsg, setReviewerMsg] = useState("");
  const [reviewerErr, setReviewerErr] = useState("");
  const [reviewerCopied, setReviewerCopied] = useState(false);

  const provisionReviewer = async () => {
    setReviewerBusy(true);
    setReviewerMsg("");
    setReviewerErr("");
    try {
      const { data } = await api.post("/admin/setup-reviewer-account", {
        email: REVIEWER_EMAIL,
        password: REVIEWER_PASSWORD,
        name: "Apple App Review",
        premium: true,
      });
      setReviewerMsg(
        `Reviewer ${data.action === "created" ? "created" : "refreshed"} · premium=${data.premium} · verified=${data.email_verified}`
      );
      // Refresh stats so the reviewer shows up in the "Premium" tile if
      // include-test-accounts is on.
      load();
    } catch (err) {
      setReviewerErr(err?.response?.data?.detail || "Couldn't provision reviewer.");
    } finally {
      setReviewerBusy(false);
    }
  };

  const copyReviewerCredentials = async () => {
    const payload = `Email: ${REVIEWER_EMAIL}\nPassword: ${REVIEWER_PASSWORD}`;
    try {
      await navigator.clipboard.writeText(payload);
      setReviewerCopied(true);
      setTimeout(() => setReviewerCopied(false), 2000);
    } catch {
      // Silent — clipboard permission edge cases only
    }
  };

  const isAdmin = user?.role === "admin";

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const [s, u] = await Promise.all([
        api.get("/admin/user-stats", { params: { include_test: includeTest } }),
        api.get("/admin/users",       { params: { include_test: includeTest, limit: 200 } }),
      ]);
      setStats(s.data);
      setUsers(u.data?.users || []);
    } catch (err) {
      setError(err?.response?.data?.detail || "Couldn't load admin data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (isAdmin) load(); /* eslint-disable-next-line */ }, [isAdmin, includeTest]);

  const maskedEmail = useMemo(() => (email) => {
    if (!maskEmails) return email;
    if (!email || !email.includes("@")) return "•••";
    const [local, domain] = email.split("@");
    const head = local.slice(0, 2);
    return `${head}${"•".repeat(Math.max(1, local.length - 2))}@${domain}`;
  }, [maskEmails]);

  // -----------------------------------------
  // Non-admin guard (double defence — backend also blocks)
  // -----------------------------------------
  if (!isAdmin) {
    return (
      <div className="screen fade-in" data-testid="admin-forbidden">
        <div className="top-bar">
          <button
            type="button"
            className="icon-btn"
            onClick={() => navigate("/")}
            aria-label="Back"
          >
            <ChevronLeft size={18} strokeWidth={1.7} />
          </button>
          <div className="logo-mark">FORBIDDEN</div>
          <div style={{ width: 32 }} />
        </div>
        <div style={{ paddingTop: 80, textAlign: "center" }}>
          <div className="h1" style={{ fontSize: 24 }}>Admins only.</div>
          <div className="body" style={{ color: "var(--text-dim)", marginTop: 12 }}>
            This page is restricted.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="screen fade-in" data-testid="admin-users-page" style={{ paddingBottom: 60 }}>
      <div className="top-bar">
        <button
          type="button"
          className="icon-btn"
          onClick={() => navigate("/")}
          aria-label="Back"
          data-testid="admin-back-btn"
        >
          <ChevronLeft size={18} strokeWidth={1.7} />
        </button>
        <div className="logo-mark">ADMIN</div>
        <button
          type="button"
          className="icon-btn"
          onClick={load}
          aria-label="Refresh"
          data-testid="admin-refresh-btn"
          disabled={loading}
        >
          <RefreshCw size={16} strokeWidth={1.7} className={loading ? "spin" : ""} />
        </button>
      </div>

      <div className="eyebrow" style={{ marginTop: 8, marginBottom: 6 }}>
        READ-ONLY · {stats?.as_of ? fmtDate(stats.as_of) : "…"}
      </div>
      <h2 className="h1" style={{ fontSize: 28, marginBottom: 18, lineHeight: 1.12 }}>
        Users
      </h2>

      {error && (
        <div
          data-testid="admin-error"
          style={{ background: "rgba(255,120,120,.12)", color: "#ffb0b0", padding: "10px 14px", borderRadius: 10, fontSize: 13, marginBottom: 14 }}
        >
          {error}
        </div>
      )}

      {/* ---------------- Stat tiles ---------------- */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 10,
          marginBottom: 20,
        }}
      >
        <StatCard
          testId="stat-total-real-users"
          icon={Users}
          label="TOTAL REAL USERS"
          value={loading || !stats ? "—" : stats.total_real_users?.toLocaleString?.() ?? stats.total_real_users}
          hint={
            stats
              ? `Excludes ${stats.excluded_admins} admin${stats.excluded_admins === 1 ? "" : "s"}${
                  stats.include_test_accounts ? "" : ` and ${stats.excluded_test_accounts} test accounts`
                }`
              : undefined
          }
        />
        <StatCard
          testId="stat-new-today"
          icon={TrendingUp}
          label="NEW TODAY"
          value={loading || !stats ? "—" : stats.new_today}
          hint="Signups since 00:00 UTC"
        />
        <StatCard
          testId="stat-new-week"
          icon={TrendingUp}
          label="LAST 7 DAYS"
          value={loading || !stats ? "—" : stats.new_last_7_days}
        />
        <StatCard
          testId="stat-new-month"
          icon={TrendingUp}
          label="LAST 30 DAYS"
          value={loading || !stats ? "—" : stats.new_last_30_days}
        />
        <StatCard
          testId="stat-premium"
          icon={Crown}
          label="PREMIUM"
          value={loading || !stats ? "—" : stats.premium_real_users}
          hint="Real users on Stripe"
        />
        <StatCard
          testId="stat-verified"
          icon={ShieldCheck}
          label="EMAIL VERIFIED"
          value={loading || !stats ? "—" : stats.verified_real_users}
        />
      </div>

      {/* ---------------- Reviewer tools ---------------- */}
      <div
        data-testid="admin-reviewer-tools-card"
        style={{
          background: "var(--bg-elev-1, #141414)",
          border: "1px solid rgba(178,138,62,0.28)",
          borderRadius: 14,
          padding: "16px 18px",
          marginBottom: 20,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
          <UserCheck size={14} strokeWidth={1.8} color="var(--gold, #b28a3e)" />
          <span
            className="eyebrow"
            style={{ fontSize: 10, letterSpacing: ".22em", color: "var(--text-dim)" }}
          >
            APPLE / PLAY REVIEWER
          </span>
        </div>
        <div style={{ fontSize: 14, color: "var(--text)", marginBottom: 4, fontWeight: 500 }}>
          Provision reviewer account
        </div>
        <div style={{ fontSize: 12, color: "var(--text-faint)", lineHeight: 1.5, marginBottom: 12 }}>
          Creates or refreshes <strong style={{ color: "var(--text-dim)" }}>{REVIEWER_EMAIL}</strong>{" "}
          with <strong style={{ color: "var(--text-dim)" }}>Premium unlocked</strong>, email verified,
          and role = user. Idempotent — safe to click any time before a submission.
        </div>

        <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
          <button
            type="button"
            onClick={provisionReviewer}
            disabled={reviewerBusy}
            data-testid="admin-provision-reviewer-btn"
            style={{
              background: "var(--gold, #b28a3e)",
              color: "#111",
              border: 0,
              borderRadius: 999,
              padding: "8px 18px",
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: ".04em",
              cursor: reviewerBusy ? "wait" : "pointer",
              opacity: reviewerBusy ? 0.6 : 1,
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            {reviewerBusy ? <RefreshCw size={12} className="spin" /> : <UserCheck size={12} />}
            {reviewerBusy ? "Provisioning…" : "Provision / refresh reviewer"}
          </button>
          <button
            type="button"
            onClick={copyReviewerCredentials}
            data-testid="admin-copy-reviewer-btn"
            style={{
              background: "transparent",
              border: "1px solid var(--line)",
              color: "var(--text-dim)",
              borderRadius: 999,
              padding: "8px 14px",
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              cursor: "pointer",
              fontSize: 12,
            }}
          >
            {reviewerCopied ? <Check size={12} /> : <Copy size={12} />}
            {reviewerCopied ? "Copied" : "Copy credentials"}
          </button>
        </div>

        {reviewerMsg && (
          <div
            data-testid="admin-reviewer-success"
            style={{
              marginTop: 10,
              background: "rgba(120,200,120,.12)",
              color: "#b9e5b9",
              padding: "8px 12px",
              borderRadius: 8,
              fontSize: 12,
            }}
          >
            {reviewerMsg}
          </div>
        )}
        {reviewerErr && (
          <div
            data-testid="admin-reviewer-error"
            style={{
              marginTop: 10,
              background: "rgba(255,120,120,.12)",
              color: "#ffb0b0",
              padding: "8px 12px",
              borderRadius: 8,
              fontSize: 12,
            }}
          >
            {reviewerErr}
          </div>
        )}
      </div>

      {/* ---------------- Controls ---------------- */}
      <div
        style={{
          display: "flex", alignItems: "center", flexWrap: "wrap", gap: 12,
          marginBottom: 12, fontSize: 12, color: "var(--text-dim)",
        }}
      >
        <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
          <input
            type="checkbox"
            checked={includeTest}
            onChange={(e) => setIncludeTest(e.target.checked)}
            data-testid="admin-include-test-toggle"
          />
          Include test accounts
        </label>
        <button
          type="button"
          onClick={() => setMaskEmails((v) => !v)}
          data-testid="admin-mask-emails-btn"
          style={{
            background: "transparent", border: "1px solid var(--line)",
            color: "var(--text-dim)", borderRadius: 999, padding: "4px 12px",
            display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer", fontSize: 12,
          }}
        >
          {maskEmails ? <Eye size={12} /> : <EyeOff size={12} />}
          {maskEmails ? "Show emails" : "Mask emails"}
        </button>
        <span style={{ marginLeft: "auto", color: "var(--text-faint)" }} data-testid="admin-users-shown-count">
          {loading ? "loading…" : `showing ${users.length}`}
        </span>
      </div>

      {/* ---------------- User list ---------------- */}
      <div
        style={{
          background: "var(--bg-elev-1, #141414)",
          border: "1px solid var(--line, #1f1f1f)",
          borderRadius: 14,
          overflow: "hidden",
        }}
      >
        {loading && !users.length ? (
          <div style={{ padding: 24, textAlign: "center", color: "var(--text-dim)", fontSize: 13 }}>
            <span className="spinner" /> Loading…
          </div>
        ) : users.length === 0 ? (
          <div style={{ padding: 24, textAlign: "center", color: "var(--text-dim)", fontSize: 13 }} data-testid="admin-users-empty">
            No users match this filter.
          </div>
        ) : (
          users.map((u, i) => (
            <div
              key={u.id || i}
              data-testid={`admin-user-row-${i}`}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "12px 14px",
                borderTop: i === 0 ? "none" : "1px solid var(--line, #1f1f1f)",
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    color: "var(--text)", fontSize: 13, fontWeight: 500,
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                  }}
                >
                  {u.name || "(no name)"}
                </div>
                <div
                  className="tiny"
                  style={{ color: "var(--text-faint)", marginTop: 3, wordBreak: "break-all" }}
                  data-testid={`admin-user-email-${i}`}
                >
                  {maskedEmail(u.email)}
                </div>
                <div className="tiny" style={{ color: "var(--text-faint)", marginTop: 3 }}>
                  Signed up {fmtDate(u.created_at)}
                </div>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-end" }}>
                {u.premium && (
                  <span
                    className="premium-pill"
                    style={{ fontSize: 9, padding: "2px 8px" }}
                    data-testid={`admin-user-premium-${i}`}
                  >
                    PREMIUM
                  </span>
                )}
                {u.email_verified && (
                  <span
                    className="premium-pill"
                    style={{
                      fontSize: 9, padding: "2px 8px",
                      borderColor: "rgba(184,255,208,0.32)", color: "var(--good, #a8e0be)",
                    }}
                  >
                    VERIFIED
                  </span>
                )}
                {u.role === "admin" && (
                  <span
                    className="premium-pill"
                    style={{ fontSize: 9, padding: "2px 8px" }}
                  >
                    ADMIN
                  </span>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      <p className="tiny" style={{ color: "var(--text-faint)", marginTop: 14, lineHeight: 1.6 }}>
        Data is read-only. Passwords are never returned by the API. Sensitive profile
        and health fields are excluded from the admin endpoints by design.
      </p>
    </div>
  );
}
